/**
 * Cloud Capacity Dashboard
 *
 * Main orchestrator component that displays cloud infrastructure capacity metrics.
 * Fetches data from ServiceMgmt API and renders summary cards, charts, and zone list.
 * Filters to show only compute zones (provider_type === 'compute').
 * Includes sync controls to trigger manual sync from ServiceMgmt to Warehouse.
 */

import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCapacities } from '../../services/capacity'
import type { CapacityResponse, CapacitySummary } from '../../types'
import CapacitySkeleton from './CapacitySkeleton'
import SummaryCards from './SummaryCards'
import ComputeMetrics from './ComputeMetrics'
import StorageMetrics from './StorageMetrics'
import ZoneList from './ZoneList'
import SyncControls from './SyncControls'

/** Filter for compute provider type */
const COMPUTE_TYPE = 'compute'

const CloudCapacityDashboard = () => {
    const { data: capacity, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CAPACITY_ALL],
            queryFn: getCapacities,
            staleTime: 2 * 60 * 1000, // 2 minutes (real-time data)
        },
        {
            loadingComponent: <CapacitySkeleton />,
        }
    )

    // Filter to compute zones only and recalculate summary
    const filteredData = useMemo((): CapacityResponse | null => {
        if (!capacity) return null

        // Filter zones to compute only
        const computeZones =
            capacity.by_zone?.filter((z) => z.provider_type === COMPUTE_TYPE) ||
            []

        // Recalculate summary from compute zones
        const summary: CapacitySummary = {
            total_zones: computeZones.length,
            total_hosts: computeZones.reduce(
                (sum, z) => sum + (z.compute.hosts?.length || 0),
                0
            ),
            healthy_hosts: computeZones.reduce(
                (sum, z) =>
                    sum +
                    (z.compute.hosts?.filter((h) => h.status === 'connected')
                        .length || 0),
                0
            ),
            total_vms: computeZones.reduce(
                (sum, z) =>
                    sum +
                    (z.compute.hosts?.reduce((s, h) => s + h.vm_count, 0) || 0),
                0
            ),
            cpu_usage_pct:
                computeZones.length > 0
                    ? computeZones.reduce(
                          (sum, z) => sum + z.compute.avg_cpu_usage_pct,
                          0
                      ) / computeZones.length
                    : 0,
            memory_usage_pct:
                computeZones.length > 0
                    ? computeZones.reduce(
                          (sum, z) => sum + z.compute.avg_memory_usage_pct,
                          0
                      ) / computeZones.length
                    : 0,
            storage_usage_pct: capacity.summary.storage_usage_pct, // Keep original storage
        }

        return {
            ...capacity,
            summary,
            by_zone: computeZones,
        }
    }, [capacity])

    // Render loading/error state
    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!filteredData) return null

    return (
        <div>
            {/* Sync Controls Header */}
            <div className="flex justify-end mb-4">
                <SyncControls />
            </div>

            {/* Summary Cards */}
            <SummaryCards summary={filteredData.summary} />

            {/* Compute Metrics */}
            <div className="mt-2">
                <ComputeMetrics compute={filteredData.compute} />
            </div>

            {/* Storage Metrics */}
            <div className="mt-2">
                <StorageMetrics storage={filteredData.storage} />
            </div>

            {/* Zone List */}
            {filteredData.by_zone && filteredData.by_zone.length > 0 && (
                <ZoneList zones={filteredData.by_zone} />
            )}
        </div>
    )
}

export default CloudCapacityDashboard
